import { Add, Close, Edit, TableRows } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { TFunction } from 'i18next';
import { Moment } from 'moment';
import { ChangeEvent, useState } from 'react';
import * as React from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SolaForceHeading } from '~/components/SolaForceHeading';
import { EmpPosition } from '~/data/employee';
import { Enum, EnumListKey } from '~/data/enums';
import { useEnumFilteredOptions } from '~/features/OverlappingEmploymentDialog/hooks/useEnumFilteredOption';
import { OverlappingEmploymentFormValues } from '~/features/OverlappingEmploymentDialog/hooks/useOverlappingEmploymentForm';
import { convertToMomentOrNull } from '~/utils/helpers/date';

import { useSxStyles } from '../useStyles';

interface Props {
  empId: number;
  empPosition: EmpPosition;
  formValues: OverlappingEmploymentFormValues;
}

export const BenefitForm = ({ formValues }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    formProps: { control, setValue },
  } = formValues;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'overlappingEmployment.benefit',
  });

  const [showRow, setShowRow] = useState(!!fields.length);

  const [currency, defaultStartDate] = useWatch({
    control,
    name: [
      'overlappingEmployment.basicData.fEmplCurrency',
      'overlappingEmployment.basicData.fEmplDateStart',
    ],
  });

  const [filteredOptionsBenefitType, filteredOptionsBenefitFrequency] =
    useEnumFilteredOptions(control, [
      EnumListKey.BENEFIT_TYPE,
      EnumListKey.EVENT_FREQUENCY,
    ]);

  const handleAddRow = () => {
    setShowRow(true);
    append({
      fBenefitProposalType: '',
      fBenefitProposalStartDate: defaultStartDate,
      fBenefitProposalEndDate: '',
      fBenefitProposalAmount: undefined,
      fBenefitProposalTaxableAmount: undefined,
      fBenefitProposalFrequency: '',
      fBenefitProposalInfo: '',
      fEmplAttachmentId: '',
      fEmplAttachmentName: '',
      fEmplAttachment: undefined,
    });
  };

  const handleRemoveRow = (row: number) => {
    remove(row);
  };

  const onCancel = () => {
    setShowRow(false);
    setValue('overlappingEmployment.benefit', []);
  };

  return (
    <Box>
      <SolaForceHeading headingIcon={<TableRows />} heading={t('laBenefit')}>
        <Button
          onClick={handleAddRow}
          startIcon={<Add />}
          style={{ marginRight: '10px' }}
        >
          {t('laAdd')}
        </Button>
        {!showRow ? (
          <Button onClick={() => setShowRow(true)} startIcon={<Edit />}>
            {t('laEdit')}
          </Button>
        ) : (
          <Button onClick={onCancel} startIcon={<Close />}>
            {t('laCancel')}
          </Button>
        )}
      </SolaForceHeading>
      {showRow && (
        <Grid sx={{ overflowX: 'auto' }}>
          <Box sx={sxStyles.table} component={'table'}>
            <Box component={'thead'}>
              <Box component={'tr'}>
                <Box component={'th'}>
                  {t('laBenefitType')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>
                  {t('laStartDate')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laEndDate')}</Box>
                <Box component={'th'}>{t('laBenefit')}</Box>
                <Box component={'th'}>{t('laTaxable')}</Box>
                <Box component={'th'}>
                  {t('laFrequency')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laBenefitInfo')}</Box>
                <Box component={'th'}>{t('laAttachment')}</Box>
                <Box component={'th'}></Box>
              </Box>
            </Box>
            <Box component={'tbody'}>
              {fields.map((_, index) => {
                return renderBenefitRow(
                  index,
                  [filteredOptionsBenefitType, filteredOptionsBenefitFrequency],
                  sxStyles,
                  formValues,
                  currency || '',
                  t,
                  handleRemoveRow,
                );
              })}
            </Box>
          </Box>
        </Grid>
      )}
    </Box>
  );
};

const renderBenefitRow = (
  index: number,
  optionsArr: Enum[][],
  sxStyles: Record<string, SxProps>,
  formValues: OverlappingEmploymentFormValues,
  currency: string,
  t: TFunction,
  onRemove: (index: number) => void,
) => {
  const dateFormat = 'YYYY-MM-DD';

  const requiredText = t('fieldIsRequired');
  const {
    formProps: { control, setValue },
  } = formValues;

  const [filteredOptionsBenefitType, filteredOptionsBenefitFrequency] =
    optionsArr;

  return (
    <Box key={index} component={'tr'}>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalType`}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={filteredOptionsBenefitType}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={
                filteredOptionsBenefitType.find((o) => o?.code === value) ||
                null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  sx={sxStyles.inputField}
                />
              )}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalStartDate`}
          control={control}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              value={convertToMomentOrNull(value)}
              onChange={(v: Moment | null) => {
                onChange(v?.format(dateFormat));
              }}
              slotProps={{
                textField: {
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  size: 'small',
                  sx: sxStyles.inputField,
                },
              }}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalEndDate`}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              value={convertToMomentOrNull(value)}
              onChange={(v: Moment | null) => {
                onChange(v?.format(dateFormat));
              }}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error?.message,
                  size: 'small',
                  sx: sxStyles.inputField,
                },
              }}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalAmount`}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="number"
              value={value}
              onChange={onChange}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">{currency}</InputAdornment>
                ),
              }}
              sx={sxStyles.inputField}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalTaxableAmount`}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="number"
              value={value}
              onChange={onChange}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">{currency}</InputAdornment>
                ),
              }}
              sx={sxStyles.inputField}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalFrequency`}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={filteredOptionsBenefitFrequency}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={
                filteredOptionsBenefitFrequency.find(
                  (o) => o?.code === value,
                ) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  error={!!error}
                  helperText={error?.message}
                  sx={sxStyles.inputField}
                />
              )}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.benefit.${index}.fBenefitProposalInfo`}
          render={({ field: { onChange, value } }) => (
            <TextField
              value={value}
              multiline
              rows={1}
              size="small"
              onChange={onChange}
              sx={sxStyles.inputField}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Grid display={'flex'} flexWrap={'nowrap'} alignItems={'center'}>
          <Controller
            control={control}
            name={`overlappingEmployment.benefit.${index}.fEmplAttachment`}
            render={({ field: { onChange, value } }) => (
              <>
                <Box
                  component={'input'}
                  type="file"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    onChange((e.target.files ?? [])[0]);
                  }}
                  id="benefit-attachment-file"
                  sx={sxStyles.hiddenFileInput}
                />
                <TextField
                  value={value?.name ?? ''}
                  size="small"
                  onChange={onChange}
                  sx={{ ...sxStyles.inputField }}
                  inputProps={{ readOnly: true }}
                />
              </>
            )}
          />

          <Box component={'label'} htmlFor="benefit-attachment-file">
            <IconButton
              size="small"
              color="primary"
              aria-label="upload picture"
              component="span"
            >
              <Box
                component={'i'}
                className="fa fa-paperclip"
                sx={sxStyles.blueIcon}
              ></Box>
            </IconButton>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setValue(
                `overlappingEmployment.benefit.${index}.fEmplAttachmentName`,
                '',
              );
              setValue(
                `overlappingEmployment.benefit.${index}.fEmplAttachment`,
                undefined,
              );
            }}
          >
            <Box
              component={'i'}
              className="fa fa-trash-o"
              sx={sxStyles.blueIcon}
            ></Box>
          </IconButton>
        </Grid>
      </Box>
      <Box component={'td'}>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          sx={sxStyles.deleteButton}
        >
          <Box component={'i'} className="fa fa-trash"></Box>
        </IconButton>
      </Box>
    </Box>
  );
};
